import { App } from "../types/app";

export interface AppGroup {
  key: string; // `${variantId}__${name}__${versionName}__${versionCode}`
  variantId: string | number;
  name: string;
  versionName: string;
  versionCode: string;
  latest: App; // newest version (display on card)
  versions: App[]; // ALL versions, ascending by upload order; index+1 = "Version N"
}

/**
 * Stable key identifying a single build. Scoped by variant: two builds in the
 * same project that share a name and version but sit under different variants
 * are different apps, not versions of one another.
 *
 * Every key here interpolates `versionCode` rather than comparing it, so records
 * that still hold it as a number (pre-migration) key identically to the strings
 * the server stores now.
 */
export function buildKey(
  app: Pick<App, "variantId" | "name" | "versionName" | "versionCode">
): string {
  return `${app.variantId ?? ""}__${app.name ?? ""}__${app.versionName ?? ""}__${app.versionCode ?? ""}`;
}

/**
 * The server's identity for a build: variant + version name + version code.
 * Uploading over an existing one of these **replaces** the record and deletes
 * the stored file, so the upload form uses this — not `buildKey` — to warn
 * before submitting. Deliberately excludes the app name, which does not
 * participate in the server's uniqueness check.
 */
export function replaceKey(
  app: Pick<App, "variantId" | "versionName" | "versionCode">
): string {
  return `${app.variantId ?? ""}__${app.versionName ?? ""}__${app.versionCode ?? ""}`;
}

/**
 * File extension of the object actually stored in S3, read off `serverFileName`
 * (e.g. "ABC1234.aab" -> "aab"). The server names every upload with a random
 * stem but keeps the real extension, since AAB/IPA/ZIP builds are downloaded
 * with the wrong extension otherwise. Falls back to "apk" only for records that
 * predate this field.
 */
export function getFileExtension(app: Pick<App, "serverFileName">): string {
  const name = app.serverFileName ?? "";
  const dot = name.lastIndexOf(".");
  return dot > -1 ? name.slice(dot + 1) : "apk";
}

/**
 * Filename to save a downloaded build under, e.g. "MyApp-1.2.0-45.aab".
 */
export function getDownloadFilename(
  app: Pick<App, "projectName" | "name" | "versionName" | "versionCode" | "serverFileName">
): string {
  const base = `${app.projectName || app.name}-${app.versionName || ""}-${app.versionCode}`;
  return `${base}.${getFileExtension(app)}`;
}

/**
 * Compare two apps by upload order. Prefers created_at; falls back to id so the
 * order is still deterministic when created_at is missing.
 */
const compareByUploadOrder = (a: App, b: App): number => {
  const at = a.created_at ? Date.parse(a.created_at) : NaN;
  const bt = b.created_at ? Date.parse(b.created_at) : NaN;
  if (!Number.isNaN(at) && !Number.isNaN(bt) && at !== bt) {
    return at - bt;
  }
  return String(a.id).localeCompare(String(b.id), undefined, { numeric: true });
};

/**
 * Turn a flat list of apps into grouped builds. Within a group, versions are
 * ascending by upload order so versions[0] is "Version 1" and the last is the
 * latest. Groups are ordered by their latest version's upload time, newest first.
 */
export function groupAppsByBuild(apps: App[]): AppGroup[] {
  const groups = new Map<string, App[]>();

  for (const app of apps) {
    const key = buildKey(app);
    const existing = groups.get(key);
    if (existing) {
      existing.push(app);
    } else {
      groups.set(key, [app]);
    }
  }

  const result: AppGroup[] = [];
  for (const [key, versions] of groups) {
    versions.sort(compareByUploadOrder);
    const latest = versions[versions.length - 1];
    result.push({
      key,
      variantId: latest.variantId,
      name: latest.name,
      versionName: latest.versionName,
      versionCode: latest.versionCode,
      latest,
      versions,
    });
  }

  result.sort((a, b) => compareByUploadOrder(b.latest, a.latest));
  return result;
}

export type BuildTypeFilter = "all" | "internal" | "external" | "beta" | "production";
export type WarehouseModeFilter = "any" | "all" | "include" | "exclude";

export interface BuildFilters {
  variantId: string | null;
  type: BuildTypeFilter;
  warehouse: WarehouseModeFilter;
}

/** Short label for a build's warehouse access, or null when the payload has none. */
export function warehouseLabel(
  app: Pick<App, "warehouse_access_mode" | "warehouse_ids">
): string | null {
  const mode = app.warehouse_access_mode;
  if (mode === undefined) return null;
  if (mode === "all") return "All";
  const count = app.warehouse_ids?.length ?? 0;
  return mode === "include" ? `Include ${count}` : `Exclude ${count}`;
}

/** True when the builds payload carries `types` at all — otherwise the Type column and filter are hidden. */
export function hasTypeData(apps: App[]): boolean {
  return apps.some((a) => Array.isArray(a.types));
}

/** True when the builds payload carries `warehouse_access_mode` at all. */
export function hasWarehouseData(apps: App[]): boolean {
  return apps.some((a) => a.warehouse_access_mode !== undefined);
}

/** Filters on each group's latest version, the one the build row displays. */
export function filterBuildGroups(
  groups: AppGroup[],
  filters: BuildFilters
): AppGroup[] {
  return groups.filter((g) => {
    if (filters.variantId !== null && String(g.variantId) !== filters.variantId) {
      return false;
    }
    if (filters.type !== "all" && !(g.latest.types ?? []).includes(filters.type)) {
      return false;
    }
    if (
      filters.warehouse !== "any" &&
      g.latest.warehouse_access_mode !== filters.warehouse
    ) {
      return false;
    }
    return true;
  });
}

export default groupAppsByBuild;
